/**
 * Pure task list helpers — no Vue/Vuex dependencies.
 * Bodies implemented incrementally as streams B/C land; safe to unit test.
 */

import appConstants from '@/plugins/constants';
import { STATUS_VARIANTS } from '@/components/utils/status-constants';
import {
  FINANCIAL_COLUMN_VALUES,
  PRIORITY_DISPLAY,
  PRIORITY_DISPLAY_NONE,
  TASKLIST_USER_TYPES,
} from './listConstants.js';
import {
  TASK_STAGES,
  TASK_STAGE_STATUS_VARIANT,
  WORKFLOW_PHASE,
  DISPLAY_STATUS_LABEL,
  CHECKLIST_STATUS_ACTION,
  LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME,
  BULK_UPLOAD_MESSAGES,
  TASK_EDIT_MESSAGES,
  TASK_UPLOAD_FILE_TYPES,
  getAccountReconUploadMismatchMessage,
  AUTO_APPROVE,
  APPROVER_TAG,
  EVIDENCE_REQUIRED,
  RETURN_TO_PENDING,
  USER_ROLE,
  USER_TYPE_HEADER_VISIBILITY,
  isEvidenceRequiredEnabled,
} from './constants.js';
import {
  isReconValidationSkipped,
  resolveEnforceThreshold,
  normalizeEnforceThresholdForTaskType,
} from './enforceThresholdHelpers.js';
import { activeUserIdsFromUserRoles } from '../views/Setup/TaskList/utils/assignmentRoles.js';

const TASK_TYPES = appConstants.TASK_LIST_CONSTANTS.TASK_TYPES;

/** Task type labels used for financial column visibility (5780). */
export const TASK_TYPE_FILTER = {
  ALL: 'all',
  ACCOUNT_RECONCILIATION: TASK_TYPES.ACCOUNT_RECONCILIATION,
  CHECKLIST: TASK_TYPES.NORMAL,
};

/** Em dash for N/A financial amounts on Checklist rows (5780). */
export const FINANCIAL_AMOUNT_NA = '—';

/**
 * Labels treated as Checklist for financial N/A (isChecklistTask).
 * Includes current display name and legacy stored "Normal Task" (pre NXTGEN-5442).
 */
const CHECKLIST_TASK_TYPE_LABELS = new Set([
  TASK_TYPE_FILTER.CHECKLIST.toLowerCase(),
  LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME.toLowerCase(),
]);

/**
 * True when the task is Checklist / Normal Task (financial amounts are N/A).
 * Accepts both stored (`Normal Task`) and display (`Checklist`) labels.
 * @param {{ task_type?: string }|string|null|undefined} taskOrType
 * @returns {boolean}
 */
export function isChecklistTask(taskOrType) {
  const taskType = typeof taskOrType === 'string' ? taskOrType : taskOrType?.task_type;
  if (taskType == null || taskType === '') return false;
  return CHECKLIST_TASK_TYPE_LABELS.has(String(taskType).trim().toLowerCase());
}

/**
 * Canonical task type for bulk-upload validation (groups legacy checklist labels).
 * @param {string|null|undefined} taskType
 * @returns {string}
 */
export function normalizeBulkUploadTaskType(taskType) {
  if (taskType == null || taskType === '') return '';
  // Canonical checklist label is "Checklist" (TASK_TYPES.NORMAL); legacy rows may store "Normal Task".
  if (isChecklistTask(taskType)) return TASK_TYPE_FILTER.CHECKLIST;
  const trimmed = String(taskType).trim();
  if (
    trimmed === TASK_TYPES.ACCOUNT_RECONCILIATION
    || trimmed.toLowerCase() === 'account reconciliation'
  ) {
    return TASK_TYPES.ACCOUNT_RECONCILIATION;
  }
  return trimmed;
}

/**
 * True when the selection mixes Account Reconciliation and Checklist tasks.
 * @param {Object[]} tasks
 * @returns {boolean}
 */
export function hasMixedBulkUploadTaskTypes(tasks = []) {
  const types = new Set(
    (Array.isArray(tasks) ? tasks : [])
      .map((task) => normalizeBulkUploadTaskType(task?.task_type))
      .filter(Boolean),
  );
  return types.size > 1;
}

/** Due-date badge states for TaskDueDateCell (5780). */
export const DUE_DATE_DISPLAY_STATE = {
  OVERDUE: 'overdue',
  DUE_TODAY: 'due_today',
  NOT_YET_DUE: 'not_yet_due',
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Normalizes a date-like value to midnight UTC for day-level comparison.
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
export function toDateOnly(value) {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

/**
 * Due-date display state and badge metadata for table cells.
 * @param {string|Date} dueDate
 * @param {string|Date} [referenceDate=new Date()]
 * @returns {{ state: string, daysLapsed: number|null, badgeLabel: string|null }}
 */
export function getDueDateDisplayState(dueDate, referenceDate = new Date()) {
  const due = toDateOnly(dueDate);
  const today = toDateOnly(referenceDate);

  if (!due || !today) {
    return { state: DUE_DATE_DISPLAY_STATE.NOT_YET_DUE, daysLapsed: null, badgeLabel: null };
  }

  const diffDays = Math.round((today.getTime() - due.getTime()) / MS_PER_DAY);

  if (diffDays > 0) {
    return {
      state: DUE_DATE_DISPLAY_STATE.OVERDUE,
      daysLapsed: diffDays,
      badgeLabel: String(diffDays),
    };
  }

  if (diffDays === 0) {
    return {
      state: DUE_DATE_DISPLAY_STATE.DUE_TODAY,
      daysLapsed: 0,
      badgeLabel: 'Today',
    };
  }

  return { state: DUE_DATE_DISPLAY_STATE.NOT_YET_DUE, daysLapsed: null, badgeLabel: null };
}

/**
 * Resolves due-date cell badge state using backend `duedays` when present, else calendar diff.
 * @param {string|Date} dueDate
 * @param {number|null|undefined} dueDays
 * @param {string|Date} [referenceDate=new Date()]
 * @param {{ suppressUrgency?: boolean }} [options]
 * @returns {{ state: string, daysLapsed: number|null, badgeLabel: string|null }}
 */
export function resolveDueDateCellDisplay(
  dueDate,
  dueDays,
  referenceDate = new Date(),
  { suppressUrgency = false } = {},
) {
  // Approved/closed tasks keep the date label but must not show Due Today / Overdue urgency.
  if (suppressUrgency) {
    return { state: DUE_DATE_DISPLAY_STATE.NOT_YET_DUE, daysLapsed: null, badgeLabel: null };
  }

  if (typeof dueDays === 'number' && Number.isFinite(dueDays)) {
    if (dueDays < 0) {
      const days = Math.abs(dueDays);
      return {
        state: DUE_DATE_DISPLAY_STATE.OVERDUE,
        daysLapsed: days,
        badgeLabel: String(days),
      };
    }
    if (dueDays === 0) {
      return {
        state: DUE_DATE_DISPLAY_STATE.DUE_TODAY,
        daysLapsed: 0,
        badgeLabel: 'Today',
      };
    }
  }

  return getDueDateDisplayState(dueDate, referenceDate);
}

/**
 * Removes legacy Due Days column — merged into Due Date (5780).
 * Filters saved layouts that may still reference `duedays`.
 * @param {Array<{ value: string, visible?: boolean, text?: string }>} headers
 * @returns {Array}
 */
export function suppressLegacyDueDaysColumn(headers) {
  if (!Array.isArray(headers)) return [];
  return headers.filter((header) => header?.value !== 'duedays');
}

/** Cycle tasklist export columns stored as boolean but displayed as Yes/No in Excel. */
export const TASKLIST_YES_NO_EXPORT_FIELDS = new Set([
  EVIDENCE_REQUIRED.FIELD,
  'auto_approve',
  'enforceThreshold',
]);

/** @param {*} value */
export function formatBooleanYesNo(value) {
  return value === true ? 'Yes' : 'No';
}

/**
 * @param {string} fieldKey
 * @param {*} value
 * @returns {boolean}
 */
function resolveTasklistYesNoEnabled(fieldKey, value) {
  if (fieldKey === EVIDENCE_REQUIRED.FIELD) {
    return isEvidenceRequiredEnabled(value);
  }
  return value === true;
}

/**
 * Preview/table display for boolean columns that should read Yes/No (not true/false).
 * @param {string} fieldKey
 * @param {*} value
 * @returns {string|*}
 */
export function formatTasklistYesNoDisplayValue(fieldKey, value) {
  if (!TASKLIST_YES_NO_EXPORT_FIELDS.has(fieldKey)) return value;
  return formatBooleanYesNo(resolveTasklistYesNoEnabled(fieldKey, value));
}

/**
 * Force Yes/No string cells so Excel does not render booleans as TRUE/FALSE.
 * evidence_required is ON unless explicitly false (legacy rows may omit the field).
 * auto_approve / skipValidations stay strict booleans (missing → No).
 * @param {string} fieldKey
 * @param {object} task
 * @returns {{ v: string, t: 's' }|null}
 */
export function formatTasklistYesNoExportCell(fieldKey, task = {}) {
  if (!TASKLIST_YES_NO_EXPORT_FIELDS.has(fieldKey)) return null;
  return {
    v: formatBooleanYesNo(resolveTasklistYesNoEnabled(fieldKey, task[fieldKey])),
    t: 's',
  };
}

/**
 * Client-side Approver-column filter with Auto Approved sentinel.
 * @param {object} task
 * @param {Array} selected
 * @param {object} userMap
 * @returns {boolean}
 */
export function taskMatchesApproverFilter(task, selected = [], userMap = {}) {
  const people = (selected || []).filter((v) => v !== AUTO_APPROVE.FILTER_VALUE);
  const includeAuto = (selected || []).includes(AUTO_APPROVE.FILTER_VALUE);
  const names = (task?.approver || []).map((id) => userMap[id]?.name || id);
  const peopleMatch = people.length > 0 && people.some((p) => names.includes(p));
  const autoMatch = includeAuto && task?.approval_state?.l1_auto_approved === true;
  if (!includeAuto && people.length === 0) return false;
  if (includeAuto && people.length === 0) return autoMatch;
  if (!includeAuto) return peopleMatch;
  return autoMatch || peopleMatch;
}

/**
 * Apply tasklist column filters, with custom Approver-column matching.
 * @param {Array} rows
 * @param {object} filters
 * @param {object} userMap
 * @param {Function} defaultFilterFn
 * @returns {Array}
 */
export function applyTasklistColumnFilters(rows, filters, userMap, defaultFilterFn) {
  if (!filters || typeof filters !== 'object' || !Object.keys(filters).length) {
    return rows;
  }
  const approverSelection = filters.approverids;
  const rest = { ...filters };
  delete rest.approverids;
  let next = rows;
  if (Object.keys(rest).length && typeof defaultFilterFn === 'function') {
    next = defaultFilterFn(next, rest);
  }
  if (Array.isArray(approverSelection) && approverSelection.length) {
    next = next.filter((task) => taskMatchesApproverFilter(task, approverSelection, userMap));
  }
  return next;
}

/**
 * Normalize a tasklist multi-value cell (assertions, tags) to trimmed strings.
 * @param {*} value
 * @returns {string[]}
 */
export function normalizeTasklistStringList(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? '').trim()).filter(Boolean);
  }
  if (value == null || value === '') return [];
  return String(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

/** Column values excluded from customize-column visibility counts (row chrome only). */
export const TASKLIST_NON_DATA_COLUMN_VALUES = new Set(['action', 'upload', 'select']);

/**
 * Whether a header is shown in the tasklist table (strict for data columns).
 * @param {{ value?: string, visible?: boolean|string|number }} header
 * @returns {boolean}
 */
export function isTasklistColumnVisible(header) {
  if (!header?.value) return false;
  const value = String(header.value).toLowerCase();
  if (TASKLIST_NON_DATA_COLUMN_VALUES.has(value)) {
    return header.visible !== false;
  }
  const visible = header.visible;
  return visible === true || visible === 'true' || visible === 1;
}

/**
 * Normalize customize-column visibility to explicit booleans for table rendering.
 * @param {Array} headers
 * @returns {Array}
 */
export function normalizeTasklistColumnLayout(headers = []) {
  if (!Array.isArray(headers)) return [];
  return headers.map((header) => {
    if (!header?.value) return header;
    return { ...header, visible: isTasklistColumnVisible(header) };
  });
}

/**
 * Resolve visibility from an active/saved header entry.
 * @param {{ visible?: boolean|string|number }} active
 * @param {{ visible?: boolean|string|number }} [defaults]
 * @returns {boolean}
 */
function resolveTasklistColumnVisibility(active, defaults = null) {
  if (Object.prototype.hasOwnProperty.call(active, 'visible')) {
    return isTasklistColumnVisible(active);
  }
  // Saved layout rows without a visibility flag are visible by convention.
  if (defaults && Object.prototype.hasOwnProperty.call(defaults, 'visible')) {
    return isTasklistColumnVisible(defaults);
  }
  return true;
}

/**
 * Build full column metadata catalog for layout merge.
 * Visibility is preserved from later sources; missing flags fall back to earlier sources, then visible.
 * Later sources override earlier metadata for the same column value.
 * @param  {...Array} sources
 * @returns {Array}
 */
export function buildTasklistColumnMergeBase(...sources) {
  const byValue = new Map();
  for (const source of sources) {
    for (const header of source || []) {
      if (header?.value == null) continue;
      const prev = byValue.get(header.value) || {};
      const merged = { ...prev, ...header };
      byValue.set(header.value, {
        ...merged,
        visible: resolveTasklistColumnVisibility(merged, prev),
        ...(prev.linkHover || header.linkHover ? { linkHover: true } : {}),
      });
    }
  }
  return suppressLegacyDueDaysColumn(Array.from(byValue.values()));
}

/**
 * Merge saved/active layout with full default columns.
 * Columns not in the saved layout inherit the default visibility (so a newly
 * added default-visible column such as Assertions appears without a reset).
 * @param {Array} defaultHeaders
 * @param {Array} activeHeaders
 * @returns {Array}
 */
export function mergeTasklistColumnLayouts(defaultHeaders = [], activeHeaders = []) {
  const defaultsByValue = new Map(
    (defaultHeaders || [])
      .filter((col) => col?.value != null)
      .map((col) => [col.value, col])
  );

  const activeByValue = new Map();
  const activeOrder = [];
  for (const col of activeHeaders || []) {
    if (col?.value == null) continue;
    if (!activeByValue.has(col.value)) {
      activeOrder.push(col.value);
    }
    activeByValue.set(col.value, col);
  }

  const orderedValues = [...activeOrder];
  for (const col of defaultHeaders || []) {
    if (col?.value == null || orderedValues.includes(col.value)) continue;
    orderedValues.push(col.value);
  }

  const merged = orderedValues.map((value) => {
    const defaults = defaultsByValue.get(value);
    const active = activeByValue.get(value);

    if (!defaults && active) {
      return {
        ...active,
        visible: resolveTasklistColumnVisibility(active, null),
      };
    }

    if (!active) {
      return { ...defaults, visible: isTasklistColumnVisible(defaults) };
    }

    return {
      ...defaults,
      ...active,
      text: active.text != null && active.text !== '' ? active.text : defaults?.text,
      filterable: active.filterable ?? defaults?.filterable,
      sortable: active.sortable ?? defaults?.sortable,
      maxWidth: active.maxWidth ?? defaults?.maxWidth,
      headerClass: active.headerClass ?? defaults?.headerClass,
      cellClass: active.cellClass ?? defaults?.cellClass,
      value_en: active.value_en ?? defaults?.value_en,
      type: active.type ?? defaults?.type,
      ...(defaults?.linkHover ? { linkHover: true } : {}),
      visible: resolveTasklistColumnVisibility(active, defaults),
    };
  });

  return normalizeTasklistColumnLayout(suppressLegacyDueDaysColumn(merged));
}

/**
 * Cycle tasklist columns from a getcustomlayout row: saved layout merged with
 * defaults, stale custom columns dropped, new ones appended, Action last.
 * @param {{ layoutData?: Object|null, defaultHeaders?: Array, customColumns?: Array }} options
 * @returns {{ headers: Array, defaultCloneHeaders: Array, defaultHeaders: Array }}
 *   headers: active layout; defaultCloneHeaders: saved layout merged with defaults;
 *   defaultHeaders: next merge base for Customize columns.
 */
export function buildTasklistHeadersFromLayout({
  layoutData = null,
  defaultHeaders = [],
  customColumns = [],
} = {}) {
  const savedLayout = layoutData?.custom_layout?.length > 0
    ? layoutData.custom_layout
    : JSON.parse(JSON.stringify(defaultHeaders || []));
  const defaultCloneHeaders = mergeTasklistColumnLayouts(defaultHeaders, savedLayout);

  const customColumnNames = (customColumns || []).map((col) => col.columnName);
  let headers = JSON.parse(JSON.stringify(defaultCloneHeaders)).filter((header) => {
    const isCustom = String(header.value_en).startsWith(appConstants.CUSTOM);
    return !isCustom || customColumnNames.includes(header.value);
  });

  (customColumns || []).forEach((col) => {
    const exists = headers.some((header) =>
      header.value === col.columnName || header.value_en === `custom_${col.columnName}`
    );
    if (!exists) {
      headers.push({
        value_en: `custom_${col.columnName}`,
        value: col.columnName,
        text: col.displayText,
        visible: true,
        filterable: true,
        sortable: true,
      });
    }
  });

  const actionIdx = headers.findIndex((header) => header.value === 'action');
  if (actionIdx !== -1) {
    const [actionCol] = headers.splice(actionIdx, 1);
    headers.push(actionCol);
  }

  headers = suppressLegacyDueDaysColumn(headers);
  return {
    headers,
    defaultCloneHeaders,
    defaultHeaders: buildTasklistColumnMergeBase(defaultHeaders, headers),
  };
}

/**
 * Role defaults for Tags / No. of Evidences. Mutates and returns `headers`.
 * Pass the raw saved `custom_layout` to keep columns that already store `visible`.
 * Omit it (persona switch) to force the role defaults.
 * @param {Array} headers
 * @param {string} userType
 * @param {Array|null} [savedLayout]
 * @returns {Array}
 */
export function applyUserTypeHeaderVisibility(headers, userType, savedLayout = null) {
  const visibility = USER_TYPE_HEADER_VISIBILITY[userType] || USER_TYPE_HEADER_VISIBILITY.manager;
  const savedWithVisibility = new Set();
  if (Array.isArray(savedLayout) && savedLayout.length > 0) {
    for (const header of savedLayout) {
      if (header?.value == null) continue;
      if (Object.prototype.hasOwnProperty.call(header, 'visible')) {
        savedWithVisibility.add(header.value);
      }
    }
  }
  for (const header of headers || []) {
    if (!header || !(header.value in visibility)) continue;
    if (savedWithVisibility.has(header.value)) continue;
    header.visible = visibility[header.value];
  }
  return headers;
}

/**
 * Same column layout (order, visibility, metadata). Headers are small plain objects.
 * @param {Array} a
 * @param {Array} b
 * @returns {boolean}
 */
export function isSameTasklistHeaders(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * True when at least one tasklist data column is visible.
 * @param {Array<{ value?: string, visible?: boolean }>} headers
 * @returns {boolean}
 */
export function hasVisibleTasklistDataColumn(headers = []) {
  return headers.some((header) => {
    if (!header?.value) return false;
    const value = String(header.value).toLowerCase();
    if (TASKLIST_NON_DATA_COLUMN_VALUES.has(value)) return false;
    return isTasklistColumnVisible(header);
  });
}

/**
 * Restore visibility flags from default headers while preserving order/custom fields.
 * @param {Array} headers
 * @param {Array} defaultHeaders
 * @returns {Array}
 */
export function restoreTasklistColumnVisibility(headers = [], defaultHeaders = []) {
  if (!Array.isArray(headers) || headers.length === 0) {
    return Array.isArray(defaultHeaders)
      ? JSON.parse(JSON.stringify(defaultHeaders))
      : [];
  }
  const defaultVisibleByValue = new Map(
    (defaultHeaders || [])
      .filter((header) => header?.value != null)
      .map((header) => [header.value, header.visible !== false])
  );
  return headers.map((header) => {
    if (!header?.value) return header;
    const value = String(header.value).toLowerCase();
    if (TASKLIST_NON_DATA_COLUMN_VALUES.has(value)) return header;
    if (defaultVisibleByValue.has(header.value)) {
      return { ...header, visible: defaultVisibleByValue.get(header.value) };
    }
    return { ...header, visible: true };
  });
}

/**
 * @param {string|null|undefined} taskTypeFilter - 'all', task type label, or null
 * @returns {boolean}
 */
export function shouldShowFinancialColumns(taskTypeFilter) {
  if (!taskTypeFilter || taskTypeFilter === TASK_TYPE_FILTER.ALL) {
    return true;
  }
  if (taskTypeFilter === TASK_TYPE_FILTER.CHECKLIST) {
    return false;
  }
  return taskTypeFilter === TASK_TYPE_FILTER.ACCOUNT_RECONCILIATION;
}

/**
 * Filters table headers by task-type filter rules (5780).
 * @param {Array<{ value: string, visible?: boolean }>} headers
 * @param {string|null|undefined} taskTypeFilter
 * @returns {Array}
 */
export function getVisibleColumns(headers, taskTypeFilter) {
  if (!Array.isArray(headers)) return [];

  const showFinancial = shouldShowFinancialColumns(taskTypeFilter);

  return headers.map((header) => {
    if (!header) return header;

    if (header.value === 'action') {
      return { ...header, visible: false };
    }

    const isFinancial = FINANCIAL_COLUMN_VALUES.includes(header.value);
    return isFinancial ? { ...header, visible: showFinancial } : { ...header };
  });
}

/**
 * Canonical priority value for column filtering (null = unset; never defaults to Low).
 * @param {string|null|undefined} priority
 * @returns {string|null}
 */
export function normalizePriorityFilterValue(priority) {
  if (priority === null || priority === undefined) return null;
  const key = String(priority).trim();
  if (!key) return null;
  const matched = Object.keys(PRIORITY_DISPLAY).find(
    (label) => label.toLowerCase() === key.toLowerCase()
  );
  return matched || key;
}

/**
 * Priority display config for a raw priority label.
 * @param {string|null|undefined} priority
 * @returns {typeof PRIORITY_DISPLAY.High}
 */
export function getPriorityDisplay(priority) {
  if (priority === null || priority === undefined) return PRIORITY_DISPLAY_NONE;
  const key = String(priority).trim();
  if (!key) return PRIORITY_DISPLAY_NONE;
  if (PRIORITY_DISPLAY[key]) return PRIORITY_DISPLAY[key];
  const matched = Object.keys(PRIORITY_DISPLAY).find(
    (label) => label.toLowerCase() === key.toLowerCase()
  );
  return matched ? PRIORITY_DISPLAY[matched] : PRIORITY_DISPLAY_NONE;
}

/** Row hover action ids (5780 matrix — extend as UX rules land). */
export const ROW_HOVER_ACTION_IDS = {
  LINK_NOA: 'link-noa',
  EDIT_PREPARER: 'edit-preparer',
  EDIT_APPROVER: 'edit-approver',
  MORE: 'more',
  APPROVE: 'approve',
  REJECT: 'reject',
  RETURN_TO_PENDING: 'return-to-pending',
  UPLOAD: 'upload',
  DOWNLOAD: 'download',
  VIEW_DETAILS: 'view-details',
  COMMENTS: 'comments',
  ACTIVITY_LOG: 'activity-log',
  DELETE: 'delete',
};

/** Display metadata for row hover action buttons. */
export const ROW_HOVER_ACTION_META = {
  [ROW_HOVER_ACTION_IDS.LINK_NOA]: {
    label: 'Link Noa Agent',
    iconType: 'img',
    iconSrc: '/img/icons/fbc-icons/LinkNoaAgent.svg',
    iconAlt: 'Link NOA Agent',
  },
  [ROW_HOVER_ACTION_IDS.EDIT_PREPARER]: {
    label: 'Edit Preparer',
    iconType: 'img',
    iconSrc: '/img/icons/fbc-icons/EditPreparer.svg',
    iconAlt: 'Edit Preparer',
  },
  [ROW_HOVER_ACTION_IDS.EDIT_APPROVER]: {
    label: 'Edit Approver',
    iconType: 'img',
    iconSrc: '/img/icons/fbc-icons/EditApprover.svg',
    iconAlt: 'Edit Approver',
  },
  [ROW_HOVER_ACTION_IDS.APPROVE]: {
    label: 'Approve',
    iconType: 'img',
    iconSrc: '/img/icons/fbc-icons/ApproveIcon.svg',
    iconAlt: 'Approve',
  },
  [ROW_HOVER_ACTION_IDS.REJECT]: {
    label: 'Reject',
    iconType: 'img',
    iconSrc: '/img/icons/fbc-icons/RejectIcon.svg',
    iconAlt: 'Reject',
  },
  [ROW_HOVER_ACTION_IDS.RETURN_TO_PENDING]: {
    label: 'Request Resubmission',
    iconType: 'mdi',
    icon: 'mdi-undo-variant',
  },
  [ROW_HOVER_ACTION_IDS.UPLOAD]: {
    label: 'Upload',
    iconType: 'img',
    iconSrc: '/img/icons/fbc-icons/UploadIcon.svg',
    iconAlt: 'Upload',
  },
  [ROW_HOVER_ACTION_IDS.DOWNLOAD]: {
    label: 'Download',
    iconType: 'img',
    iconSrc: '/img/icons/fbc-icons/DownloadIcon.svg',
    iconAlt: 'Download',
  },
  [ROW_HOVER_ACTION_IDS.VIEW_DETAILS]: {
    label: 'View Task',
    iconType: 'mdi',
    icon: 'mdi-information-outline',
  },
  [ROW_HOVER_ACTION_IDS.MORE]: {
    label: 'More',
    iconType: 'img',
    iconSrc: '/img/icons/fbc-icons/MoreActionsIcon.svg',
    iconAlt: 'More actions',
  },
};

const REJECTED_STAGE = -1;

function isTaskStage(task, stage) {
  return stagesMatch(task?.stage, stage);
}

function isClosedTask(task) {
  return isTaskStage(task, TASK_STAGES.CLOSED);
}

function isPendingApprovalTask(task) {
  return isTaskStage(task, TASK_STAGES.PENDING_APPROVAL);
}

function isPendingSubmissionTask(task) {
  return isTaskStage(task, TASK_STAGES.PENDING_SUBMISSION);
}

function isRejectedTask(task) {
  return isTaskStage(task, REJECTED_STAGE);
}

function canShowEditPreparer(task) {
  return !isClosedTask(task);
}

function canShowEditApprover(task) {
  return !isClosedTask(task);
}

/**
 * Edit is locked when the task is end-approved (stage 3).
 * L1 pending and L2 pending both stay on stage 2; only a finished approval moves to 3.
 * @param {Object} task
 * @returns {boolean}
 */
export function isApprovedTaskEditLocked(task) {
  if (!task) return false;
  return isClosedTask(task);
}

/**
 * True when any selected task cannot be edited (bulk/multi-select).
 * @param {Object[]} tasks
 * @returns {boolean}
 */
export function hasApprovedTaskEditLocked(tasks = []) {
  return (Array.isArray(tasks) ? tasks : []).some((task) => isApprovedTaskEditLocked(task));
}

function canShowApproverDecisionActions(task) {
  return isPendingApprovalTask(task);
}

export function canPreparerUploadToTask(task) {
  return isPendingSubmissionTask(task) || isRejectedTask(task);
}

/**
 * True when bulk upload is blocked: mixed AR+Checklist selection, or a task
 * that is not pending submission / rejected.
 * @param {Object[]} tasks
 * @returns {boolean}
 */
export function hasPreparerUploadBlocked(tasks = []) {
  return hasMixedBulkUploadTaskTypes(tasks)
    || (Array.isArray(tasks) ? tasks : []).some((task) => !canPreparerUploadToTask(task));
}

/**
 * User-facing message when bulk preparer upload is blocked.
 * Mixed selections (e.g. pending submission + pending approval) get a specific message.
 * @param {Object[]} tasks
 * @returns {string}
 */
export function getPreparerBulkUploadBlockMessage(tasks = []) {
  const list = Array.isArray(tasks) ? tasks : [];
  if (hasMixedBulkUploadTaskTypes(list)) {
    return TASK_EDIT_MESSAGES.UPLOAD_MIXED_TASK_TYPES;
  }
  if (!hasPreparerUploadBlocked(list)) return '';

  const hasUploadable = list.some(canPreparerUploadToTask);
  const hasBlocked = list.some((task) => !canPreparerUploadToTask(task));

  if (hasUploadable && hasBlocked) {
    return TASK_EDIT_MESSAGES.UPLOAD_MIXED_SELECTION;
  }

  return TASK_EDIT_MESSAGES.UPLOAD_LOCKED;
}

function canShowDownload(task) {
  return taskHasDownloadableFiles(task);
}

/**
 * Mirrors utils.extractFileIdsFromTask / hasDownloadableFiles so hover Download
 * visibility matches what the download path can actually resolve.
 * Supports upload_v2 objects and legacy upload as string | flat | nested arrays.
 */
function taskHasDownloadableFiles(task) {
  if (!task) return false;

  if (Array.isArray(task.upload_v2) && task.upload_v2.length > 0) {
    return task.upload_v2.some((file) => {
      const id = file?.file_id || file?._id;
      return Boolean(id && typeof id === 'string');
    });
  }

  if (task.upload) {
    if (Array.isArray(task.upload)) {
      return task.upload.flat(Infinity).some((id) => id && typeof id === 'string');
    }
    if (typeof task.upload === 'string') {
      return task.upload.length > 0;
    }
  }

  return false;
}

/**
 * Persona × status hover actions for a single task row (5780).
 * @param {string} persona - manager | preparer | approver
 * @param {Object} task
 * @param {{ userid?: string, metadata?: { managerlist?: string[] } }} [context]
 * @returns {string[]} action ids
 */
export function getRowHoverActions(persona, task = {}) {
  const actions = [];

  if (persona === TASKLIST_USER_TYPES.MANAGER) {
    if (canShowEditPreparer(task)) {
      actions.push(ROW_HOVER_ACTION_IDS.EDIT_PREPARER);
    }
    if (canShowEditApprover(task)) {
      actions.push(ROW_HOVER_ACTION_IDS.EDIT_APPROVER);
    }
    actions.push(ROW_HOVER_ACTION_IDS.MORE);
    return actions;
  }

  if (persona === TASKLIST_USER_TYPES.APPROVER) {
    if (canShowApproverDecisionActions(task)) {
      actions.push(ROW_HOVER_ACTION_IDS.APPROVE);
      actions.push(ROW_HOVER_ACTION_IDS.REJECT);
    } else if (shouldUseReturnToPendingAction(task)) {
      actions.push(ROW_HOVER_ACTION_IDS.REJECT);
    } else {
      actions.push(ROW_HOVER_ACTION_IDS.VIEW_DETAILS);
    }
    if (canShowDownload(task)) {
      actions.push(ROW_HOVER_ACTION_IDS.DOWNLOAD);
    }
    actions.push(ROW_HOVER_ACTION_IDS.MORE);
    return actions;
  }

  if (persona === TASKLIST_USER_TYPES.PREPARER) {
    if (canPreparerUploadToTask(task)) {
      actions.push(ROW_HOVER_ACTION_IDS.UPLOAD);
    }
    if (canShowDownload(task)) {
      actions.push(ROW_HOVER_ACTION_IDS.DOWNLOAD);
    }
    actions.push(ROW_HOVER_ACTION_IDS.MORE);
    return actions;
  }

  return [ROW_HOVER_ACTION_IDS.MORE];
}

/**
 * Compares pipeline stage values, coercing numeric strings (e.g. 2 vs '2').
 * @param {*} taskStage
 * @param {*} selectedStage
 * @returns {boolean}
 */
function stagesMatch(taskStage, selectedStage) {
  if (taskStage === selectedStage) return true;
  if (taskStage == null || selectedStage == null) return false;

  const taskNum = Number(taskStage);
  const selectedNum = Number(selectedStage);
  if (Number.isFinite(taskNum) && Number.isFinite(selectedNum)) {
    return taskNum === selectedNum;
  }

  return String(taskStage) === String(selectedStage);
}

function normalizeTaskUserIds(raw) {
  if (raw == null || raw === '') return [];
  const list = Array.isArray(raw) ? raw : [raw];
  return list
    .map((id) => {
      if (id == null || id === '') return '';
      if (typeof id === 'object') {
        const resolved = id._id || id.id || id.user_id;
        return resolved != null && resolved !== '' ? String(resolved) : '';
      }
      return String(id);
    })
    .filter(Boolean);
}

/**
 * True when user is assigned as L1 approver or L2 sign-off approver on a task.
 * Prefers `user_roles` when present; falls back to flat approver / sign_off fields.
 * @param {Object} task
 * @param {string} userId
 * @returns {boolean}
 */
export function isUserApproverOnTask(task, userId) {
  if (!task || userId == null || userId === '') return false;
  const uid = String(userId);
  if (Array.isArray(task.user_roles) && task.user_roles.length) {
    const l1 = activeUserIdsFromUserRoles(task.user_roles, USER_ROLE.APPROVER, APPROVER_TAG.L1);
    const l2 = activeUserIdsFromUserRoles(task.user_roles, USER_ROLE.APPROVER, APPROVER_TAG.L2);
    return l1.includes(uid) || l2.includes(uid);
  }
  const approverIds = normalizeTaskUserIds(task.approver);
  if (approverIds.includes(uid)) return true;
  return normalizeTaskUserIds(task.sign_off_approvers).includes(uid);
}

/**
 * True when task is in stage 2 awaiting L2 sign-off (API projection or approval_state).
 * Display label is not used to tell L1 wait from L2 wait (both show Pending Approval).
 * @param {Object} task
 * @returns {boolean}
 */
export function isAwaitingSignOff(task) {
  if (!task) return false;
  if (task.workflow_phase === WORKFLOW_PHASE.AWAITING_SIGN_OFF) return true;
  const approvalState = task.approval_state || {};
  return stagesMatch(task.stage, TASK_STAGES.PENDING_APPROVAL)
    && approvalState.l1_completed === true
    && approvalState.l2_required === true
    && approvalState.l2_completed !== true;
}

/**
 * True when reject in the UI must call return_to_pending (post-L1 / closed).
 * Approve or sign-off actions take precedence — show Reject, not request resubmission.
 * @param {Object} task
 * @returns {boolean}
 */
export function shouldUseReturnToPendingAction(task) {
  const caps = task?.capabilities || {};
  if (caps.can_l1_approve || caps.can_sign_off) return false;
  return !!caps.can_return_to_pending;
}

/**
 * Remark payload for changecheckliststage.
 * Request resubmission is stored on its own field so it does not replace the approver remark.
 * @param {string} status CHECKLIST_STATUS_ACTION value
 * @param {string} trimmedRemark
 * @returns {{ approver_remark: string } | { resubmission_remark: string }}
 */
export function buildChecklistStatusRemarkPayload(status, trimmedRemark) {
  if (status === CHECKLIST_STATUS_ACTION.RETURN_TO_PENDING) {
    return { [RETURN_TO_PENDING.REMARK_FIELD]: trimmedRemark };
  }
  return { approver_remark: trimmedRemark };
}

/**
 * True when a task can be included in a Reject action.
 * Closed tasks are eligible only when return-to-pending is the server capability.
 * @param {Object} task
 * @returns {boolean}
 */
export function isEligibleForRejectAction(task) {
  if (!task) return false;
  if (shouldUseReturnToPendingAction(task)) return true;
  return !isClosedTask(task);
}

/**
 * Resolve changecheckliststage action type from task workflow state.
 * Awaiting L1 → approve; awaiting L2 sign-off → signoff.
 * Reject on a return-to-pending-capable task → return_to_pending.
 * @param {Object} task
 * @param {'approve'|'reject'|'return_to_pending'} mode — use CHECKLIST_STATUS_ACTION
 * @returns {string} CHECKLIST_STATUS_ACTION value (approve | signoff | reject | return_to_pending)
 */
export function resolveChecklistStatusActionType(task, mode = CHECKLIST_STATUS_ACTION.APPROVE) {
  if (mode === CHECKLIST_STATUS_ACTION.REJECT || mode === CHECKLIST_STATUS_ACTION.RETURN_TO_PENDING) {
    if (shouldUseReturnToPendingAction(task)) return CHECKLIST_STATUS_ACTION.RETURN_TO_PENDING;
    return CHECKLIST_STATUS_ACTION.REJECT;
  }

  const api = task?.capabilities;
  if (api?.can_sign_off) return CHECKLIST_STATUS_ACTION.SIGNOFF;
  if (api?.can_l1_approve) return CHECKLIST_STATUS_ACTION.APPROVE;
  if (isAwaitingSignOff(task)) return CHECKLIST_STATUS_ACTION.SIGNOFF;

  return CHECKLIST_STATUS_ACTION.APPROVE;
}

/**
 * Group tasks by changecheckliststage action type for bulk approve/sign-off/reject.
 * @param {Object[]} tasks
 * @param {'approve'|'reject'|'return_to_pending'} mode
 * @returns {Array<{ actionType: string, tasks: object[] }>}
 */
export function groupTasksByStatusActionType(tasks, mode = CHECKLIST_STATUS_ACTION.APPROVE) {
  if (!Array.isArray(tasks) || tasks.length === 0) return [];

  const byType = new Map();
  for (const task of tasks) {
    const actionType = resolveChecklistStatusActionType(task, mode);
    if (!byType.has(actionType)) byType.set(actionType, []);
    byType.get(actionType).push(task);
  }

  return Array.from(byType.entries()).map(([actionType, groupedTasks]) => ({
    actionType,
    tasks: groupedTasks,
  }));
}

/**
 * Stage chip label. L1 wait and L2 wait both show Pending Approval.
 * @param {Object} task
 * @returns {string}
 */
export function getTaskStageDisplayLabel(task) {
  if (!task) return '';
  if (task.display_status_label) return task.display_status_label;
  if (isAwaitingSignOff(task)) return DISPLAY_STATUS_LABEL.PENDING_APPROVAL;
  return '';
}

/**
 * StatusIndicator variant for a cycle task. Resubmission uses amber ,
 * not the grey pending-submission chip.
 * @param {Object} task
 * @returns {string}
 */
export function getTaskStageStatusVariant(task) {
  if (getTaskStageDisplayLabel(task) === DISPLAY_STATUS_LABEL.RESUBMISSION) {
    return STATUS_VARIANTS.FC_RESUBMISSION;
  }
  return TASK_STAGE_STATUS_VARIANT[task?.stage] || STATUS_VARIANTS.UNKNOWN;
}

/**
 * True when L1 was system auto-approved, then returned or rejected, and human L1
 * has not approved again. Drives the reject banner through preparer resubmit.
 * @param {Object} task
 * @returns {boolean}
 */
export function isAutoApprovedResubmission(task = {}) {
  const flagged = (
    task?.approval_state?.auto_approved_resubmission === true
    || task?.auto_approved_resubmission === true
  );
  if (!flagged) return false;
  if (task?.approval_state?.l1_completed === true) return false;
  const label = getTaskStageDisplayLabel(task);
  if (label === DISPLAY_STATUS_LABEL.APPROVED || label === 'Approved') return false;
  if (task?.workflow_phase === WORKFLOW_PHASE.AWAITING_SIGN_OFF) return false;
  if (task?.workflow_phase === WORKFLOW_PHASE.CLOSED) return false;
  return !isTaskStage(task, TASK_STAGES.CLOSED);
}

/**
 * Banner body for auto-approved resubmission (Figma 5680:207972).
 * @param {{ actorName?: string, dateLabel?: string }} [parts]
 * @returns {string}
 */
function fillBannerTemplate(template, actorName, dateLabel) {
  return template
    .replace(/\{actor\}/g, actorName)
    .replace(/\{date\}/g, dateLabel);
}

export function getAutoApprovedResubmissionBannerBody(parts = {}) {
  const actorName = (parts.actorName || '').trim();
  const dateLabel = (parts.dateLabel || '').trim();
  if (actorName && dateLabel) {
    return fillBannerTemplate(AUTO_APPROVE.RESUBMISSION_BANNER_ACTOR_DATE, actorName, dateLabel);
  }
  if (actorName) {
    return fillBannerTemplate(AUTO_APPROVE.RESUBMISSION_BANNER_ACTOR_ONLY, actorName, dateLabel);
  }
  if (dateLabel) {
    return fillBannerTemplate(AUTO_APPROVE.RESUBMISSION_BANNER_DATE_ONLY, actorName, dateLabel);
  }
  return AUTO_APPROVE.RESUBMISSION_BANNER_FALLBACK;
}

/**
 * Filters tasks by pipeline stage chip selection.
 * @param {Array} tasks
 * @param {number|string|null} selectedStage - null = All
 * @returns {Array}
 */
export function filterByPipelineStage(tasks, selectedStage) {
  if (!Array.isArray(tasks)) return [];
  if (selectedStage === null || selectedStage === undefined) {
    return [...tasks];
  }
  return tasks.filter((task) => stagesMatch(task?.stage, selectedStage));
}

/**
 * Case-insensitive search against task description (Task Name field).
 * @param {Array} tasks
 * @param {string} searchTerm
 * @returns {Array}
 */
export function filterByTaskNameSearch(tasks, searchTerm) {
  if (!Array.isArray(tasks)) return [];
  const term = (searchTerm || '').trim().toLowerCase();
  if (!term) return [...tasks];
  return tasks.filter((task) =>
    (task?.description || '').toLowerCase().includes(term)
  );
}

/**
 * True when a rejection remark is non-empty after trim and contains at least one letter or number.
 * Rejects special-character-only input (e.g. @#$%^&*). Uses Unicode letter/number classes so
 * non-Latin scripts (accented Latin, CJK, etc.) are accepted.
 * @param {string|null|undefined} remark
 * @returns {boolean}
 */
export function isValidRejectionRemark(remark) {
  const trimmed = (remark || '').trim();
  if (!trimmed) return false;
  return /[\p{L}\p{N}]/u.test(trimmed);
}

/**
 * Count of files already on a task (`upload_v2`).
 * @param {Object} task
 * @returns {number}
 */
export function getTaskExistingUploadCount(task) {
  const uploads = task?.upload_v2;
  return Array.isArray(uploads) ? uploads.length : 0;
}

/**
 * Legacy upload IDs — string | flat | nested arrays (mirrors extractFileIdsFromTask).
 * @param {string|string[]|Array[]} upload
 * @returns {string[]}
 */
function normalizeLegacyUploadIds(upload) {
  if (!upload) return [];
  if (Array.isArray(upload)) {
    return upload
      .flat(Infinity)
      .filter((id) => id != null && id !== '' && typeof id === 'string');
  }
  if (typeof upload === 'string' && upload.length > 0) {
    return [upload];
  }
  return [];
}

/**
 * Normalized upload entries for validation — prefers upload_v2, falls back to legacy upload IDs.
 * @param {Object} task
 * @returns {Array<{ file_id?: string, file_type?: string }>}
 */
export function getTaskUploadEntries(task = {}) {
  if (Array.isArray(task?.upload_v2) && task.upload_v2.length > 0) {
    return task.upload_v2;
  }

  return normalizeLegacyUploadIds(task?.upload).map((id) => ({
    file_id: id,
    file_type: TASK_UPLOAD_FILE_TYPES.NORMAL,
  }));
}

/**
 * Count evidence vs recon uploads on a task.
 * @param {Object} task
 * @returns {{ evidence: number, recon: number, total: number }}
 */
export function countTaskUploadsByType(task = {}) {
  const entries = getTaskUploadEntries(task);
  let evidence = 0;
  let recon = 0;

  entries.forEach((file) => {
    if (file?.file_type === TASK_UPLOAD_FILE_TYPES.RECON) {
      recon += 1;
    } else {
      evidence += 1;
    }
  });

  return { evidence, recon, total: entries.length };
}

function isTaskBotLinked(task = {}) {
  return !!(task?.botid || task?.bot_details?._id);
}

/**
 * Client-side AR submit validation — mirrors backend processTaskSubmission upload rules.
 * @param {Object} task
 * @returns {string|null} Error message when invalid; null when OK or validation skipped.
 */
export function validateAccountReconTaskSubmission(task = {}) {
  if (!isAccountReconciliationTask(task)) {
    return null;
  }

  const skipReconValidations = isReconValidationSkipped(task);
  const evidenceRequired = getTaskEvidenceRequired(task);
  const botLinked = isTaskBotLinked(task);
  const { evidence, recon } = countTaskUploadsByType(task);
  const enforceReconFile = !skipReconValidations && !botLinked;

  if (evidenceRequired > 0 && evidence < evidenceRequired) {
    const reconAlsoMissing = enforceReconFile && recon < 1;
    return getAccountReconUploadMismatchMessage({
      evidenceRequired,
      reconRequired: reconAlsoMissing ? 1 : 0,
      evidenceFound: evidence,
      reconFound: recon,
    });
  }

  if (skipReconValidations || botLinked) {
    return null;
  }

  if (evidenceRequired === 0) {
    if (recon !== 1) {
      return getAccountReconUploadMismatchMessage({
        evidenceRequired: 0,
        reconRequired: 1,
        evidenceFound: evidence,
        reconFound: recon,
      });
    }
    return null;
  }

  if (recon < 1) {
    return getAccountReconUploadMismatchMessage({
      evidenceRequired: 0,
      reconRequired: 1,
      evidenceFound: evidence,
      reconFound: recon,
    });
  }

  return null;
}

export { resolveEnforceThreshold, isReconValidationSkipped, normalizeEnforceThresholdForTaskType };

/**
 * Submit-time evidence gate (mirrors supernova isEvidenceCountEnforced).
 * @param {Object} task
 * @returns {boolean}
 */
export function isEvidenceCountEnforced(task = {}) {
  return isEvidenceRequiredEnabled(task?.evidence_required) && Number(task?.no_of_evidence) > 0;
}

/**
 * Normalized evidence requirement for submit/upload UI.
 * @param {Object} task
 * @returns {number}
 */
export function getTaskEvidenceRequired(task) {
  return isEvidenceCountEnforced(task) ? Number(task.no_of_evidence) : 0;
}

/**
 * Additional files still needed on a task before it can be submitted.
 * @param {Object} task
 * @returns {number}
 */
export function getTaskAdditionalEvidenceNeeded(task) {
  const required = getTaskEvidenceRequired(task);
  if (!required) return 0;
  return Math.max(0, required - getTaskExistingUploadCount(task));
}

/**
 * Highest no_of_evidence across selected bulk-upload tasks (gross requirement).
 * @param {Array|Object} tasks
 * @returns {number}
 */
export function getMaxEvidenceRequired(tasks = []) {
  const list = Array.isArray(tasks) ? tasks : tasks ? [tasks] : [];
  let max = 0;
  list.forEach((task) => {
    const required = getTaskEvidenceRequired(task);
    if (required > max) max = required;
  });
  return max;
}

/**
 * Max additional files still needed across selected tasks (accounts for existing uploads).
 * @param {Array|Object} tasks
 * @returns {number}
 */
export function getMaxAdditionalEvidenceRequired(tasks = []) {
  const list = Array.isArray(tasks) ? tasks : tasks ? [tasks] : [];
  let max = 0;
  list.forEach((task) => {
    const additional = getTaskAdditionalEvidenceNeeded(task);
    if (additional > max) max = additional;
  });
  return max;
}

/**
 * True when every selected task satisfies: existing uploads + new files >= required evidence.
 * @param {Array|Object} tasks
 * @param {number} newFilesCount
 * @returns {boolean}
 */
export function canBulkSubmitByEvidence(tasks, newFilesCount = 0) {
  const list = Array.isArray(tasks) ? tasks : tasks ? [tasks] : [];
  const newCount = Number(newFilesCount) || 0;
  if (!list.length) return true;
  return list.every((task) => {
    const required = getTaskEvidenceRequired(task);
    if (!required) return true;
    return getTaskExistingUploadCount(task) + newCount >= required;
  });
}

/**
 * True when a task is Account Reconciliation (AR).
 * @param {Object} task
 * @returns {boolean}
 */
export function isAccountReconciliationTask(task) {
  return task?.task_type === TASK_TYPES.ACCOUNT_RECONCILIATION;
}

/**
 * True when every task in a bulk selection is AR (bulk upload disallows mixed types).
 * @param {Array|Object} tasks
 * @returns {boolean}
 */
export function isBulkSelectionAccountReconciliation(tasks = []) {
  const list = Array.isArray(tasks) ? tasks : tasks ? [tasks] : [];
  if (!list.length) return false;
  return list.every((task) => isAccountReconciliationTask(task));
}

/**
 * True when bulk workpaper upload should auto-submit after save.
 * AR tasks are always save-only from bulk upload; checklist tasks require evidence.
 * @param {Array|Object} tasks
 * @param {number} newFilesCount
 * @returns {boolean}
 */
export function canBulkAutoSubmit(tasks, newFilesCount = 0) {
  if (isBulkSelectionAccountReconciliation(tasks)) return false;
  return canBulkSubmitByEvidence(tasks, newFilesCount);
}

/** @deprecated Use canBulkSubmitByEvidence — kept as alias for existing imports. */
export function canBulkSubmitByMaxEvidence(tasks, newFilesCount = 0) {
  return canBulkSubmitByEvidence(tasks, newFilesCount);
}

/**
 * Generic orange warning after files are selected but per-task evidence is not satisfied.
 * Submit check uses existing uploads + new files per task; pre-upload banner count uses gross max required.
 * @param {Array|Object} tasks
 * @param {number} newFilesCount
 * @param {string} message
 * @returns {string}
 */
export function getBulkEvidenceMismatchWarning(
  tasks,
  newFilesCount = 0,
  message = BULK_UPLOAD_MESSAGES.EVIDENCE_NOT_MATCHED
) {
  const list = Array.isArray(tasks) ? tasks : tasks ? [tasks] : [];
  const newCount = Number(newFilesCount) || 0;
  if (!newCount || getMaxAdditionalEvidenceRequired(list) <= 0) return '';
  if (canBulkSubmitByEvidence(list, newCount)) return '';
  return message;
}

/** @deprecated Use getBulkEvidenceMismatchWarning */
export function getBulkEvidenceWarningMessage(additionalRequired, uploadedCount = 0) {
  const required = Number(additionalRequired) || 0;
  const uploaded = Number(uploadedCount) || 0;
  if (required <= 0 || uploaded >= required) return '';
  const requiredLabel = required === 1 ? 'evidence file' : 'evidence files';
  const uploadVerb = uploaded === 1 ? 'is' : 'are';
  return `${required} ${requiredLabel} required but only ${uploaded} ${uploadVerb} uploaded, so it won't be submitted but saved`;
}

/**
 * Placeholder for formatted row extraction — full implementation moves here in Stream B.
 * @returns {null}
 */
export function formatTasklistRowPlaceholder() {
  return null;
}
